import * as React from "react"

import { Slot } from "@radix-ui/react-slot"

import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",

  {
    variants: {
      variant: {
        // Original variants

        default: "bg-primary text-primary-foreground hover:bg-primary/90",

        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",

        outline: "border border-input hover:bg-accent hover:text-accent-foreground",

        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[#E1EEFF] active:bg-[#024FAC] active:border-[#024FAC] active:text-white border border-secondary-foreground",

        ghost: "hover:bg-accent hover:text-accent-foreground",

        link: "text-primary underline-offset-4 shadow-none hover:underline",

        callToAction:
          "text-accent-foreground border border-input bg-gray-200 hover:bg-accent hover:text-accent-foreground",

        // Branding variants (Orange theme)

        branding:
          "bg-[var(--color-orange-500)] text-[var(--color-white)] hover:bg-[var(--color-orange-600)] focus-visible:ring-[var(--color-orange-400)] active:bg-[var(--color-orange-600)]",

        "branding-subtle":
          "border border-[var(--color-orange-500)] text-[var(--color-orange-500)] hover:bg-[var(--color-orange-50)] focus-visible:ring-[var(--color-orange-400)] active:bg-[var(--color-orange-500)] active:text-[var(--color-white)]",

        // Primary variants (Blue theme)

        "primary-default":
          "bg-[var(--color-blue-460)] text-[var(--color-white)] hover:bg-[var(--color-blue-560)] focus-visible:ring-[var(--color-blue-360)] active:bg-[var(--color-blue-660)]",

        "primary-subtle":
          "border border-[var(--color-blue-460)] text-[var(--color-blue-460)] hover:bg-[var(--color-blue-60)] focus-visible:ring-[var(--color-blue-360)] active:bg-[var(--color-blue-60)] active:ring active:ring-offset-3 active:ring-[var(--color-blue-460)]",

        // Highlight variants (Minimal blue)

        highlight:
          "text-[var(--color-blue-460)] hover:text-[var(--color-blue-560)] focus-visible:ring-[var(--color-blue-360)] focus-visible:bg-[var(--color-blue-60)] active:bg-[var(--color-blue-460)] active:text-[var(--color-white)] active:rounded-md",

        // Secondary variants (Gray theme)

        "secondary-default":
          "border border-[var(--color-neutral-300)] text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-50)] focus-visible:ring-[var(--color-neutral-400)] active:bg-[var(--color-neutral-150)]",

        "secondary-subtle":
          "hover:bg-black-5 focus-visible:bg-black-10 active:bg-bg-black-10",

        // Success variants (Green theme)

        success:
          "bg-[var(--color-green-600)] text-[var(--color-white)] hover:bg-[var(--color-green-700)] focus-visible:ring-[var(--color-green-400)] active:bg-[var(--color-green-800)]",

        "success-subtle":
          "border border-[var(--color-green-600)] text-[var(--color-green-600)] hover:bg-[var(--color-green-100)] focus-visible:ring-[var(--color-green-400)] active:bg-[var(--color-green-600)] active:text-[var(--color-white)]",

        // Warning variants (Yellow theme)

        warning:
          "bg-[var(--color-yellow-450)] text-[var(--color-yellow-850)] hover:bg-[var(--color-yellow-550)] focus-visible:ring-[var(--color-yellow-350)] active:bg-[var(--color-yellow-550)]",

        "warning-subtle":
          "border border-[var(--color-yellow-450)] text-[var(--color-yellow-550)] hover:bg-[var(--color-yellow-150)] focus-visible:ring-[var(--color-yellow-350)] active:bg-[var(--color-yellow-450)] active:text-[var(--color-yellow-850)]",

        // Destructive variants (Red theme)

        "destructive-default":
          "bg-[var(--color-red-450)] text-[var(--color-white)] hover:bg-[var(--color-red-550)] focus-visible:ring-[var(--color-red-150)] active:bg-[var(--color-red-650)]",

        "destructive-subtle":
          "border border-[var(--color-red-450)] text-[var(--color-red-450)] hover:bg-[var(--color-red-50)] focus-visible:ring-[var(--color-red-150)] active:bg-[var(--color-red-450)] active:text-[var(--color-white)]",

        // Inline variants (Minimal styling)

        "inline-default":
          "border border-[var(--color-neutral-300)] text-[var(--color-neutral-850)] hover:bg-black-5 active:bg-black-10",

        "inline-subtle":
          "text-[var(--color-neutral-850)] hover:text-[var(--color-neutral-850)] hover:bg-black-5 hover:rounded-md focus-visible:ring-[var(--color-neutral-400)] focus-visible:bg-black-5 active:bg-black-10",
      },

      size: {
        default: "h-10 px-4 py-2",

        xs: "h-6 rounded-sm px-2",

        sm: "h-8 rounded-md px-3",

        lg: "h-11 rounded-md px-8",

        icon: "h-10 w-10",

        "icon-sm": "h-8 w-8",

        "icon-sm-circle": "h-8 w-8 rounded-full",
      },
    },

    defaultVariants: {
      variant: "default",

      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  },
)

Button.displayName = "Button"

export { Button, buttonVariants }
